import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, map, catchError, of } from 'rxjs';
import { CandidateService } from 'src/app/recruiting/candidates/candidate.service';
import {
    NoteAndActivityTypeEnum,
    UserTypeEnum
} from 'src/app/recruiting/candidates/new-profile/models/notes-and-activities.enum';
import {
    NotesAndActivities,
    NotesAndActivitiesOrientationNote
} from 'src/app/recruiting/candidates/new-profile/models/notes-and-activities.model';
import { NotesHelper } from 'src/app/recruiting/candidates/new-profile/notes-and-activities/store/notes-helper';
import * as NoteActions from 'src/app/recruiting/candidates/new-profile/notes-and-activities/store/notes.actions';
import {
    selectCandidateId,
    selectLimitHistory,
    selectNotesAndActivities,
    selectOrientationNotes
} from 'src/app/recruiting/candidates/new-profile/notes-and-activities/store/notes.selectors';
import { NotesState } from 'src/app/recruiting/candidates/new-profile/notes-and-activities/store/notes.state';
import { HttpResponseHelpers } from 'src/app/shared/helpers';

@Injectable()
export class NotesEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _candidateService: CandidateService,
        private readonly _sanitizer: DomSanitizer,
        private readonly _store: Store<NotesState>
    ) {}

    loadNotesAndActivities$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoteActions.loadNotesAndActivities),
            concatLatestFrom(() => [
                this._store.select(selectNotesAndActivities),
                this._store.select(selectOrientationNotes),
                this._store.select(selectCandidateId),
                this._store.select(selectLimitHistory)
            ]),
            switchMap(([action, latestNotes, latestOrientationNotes, previousCandidateId, limitHistory]) => {
                if (
                    latestNotes &&
                    latestNotes.length > 0 &&
                    action.limitHistory === limitHistory &&
                    action.candidateId === previousCandidateId
                ) {
                    return of(
                        NoteActions.loadNotesAndActivitiesSuccess({
                            notesAndActivities: latestNotes,
                            candidateId: action.candidateId,
                            limitHistory: action.limitHistory,
                            orientationNotes: latestOrientationNotes
                        })
                    );
                }

                return this._candidateService
                    .getCandidateNotesAndActivities(action.candidateId, action.limitHistory)
                    .pipe(
                        switchMap((data) => {
                            const notesAndActivities = data.profileNotes;
                            const orientationNotes = data.orientationNotes;
                            notesAndActivities.forEach((item) => {
                                NotesHelper.shapeNote(
                                    item as NotesAndActivitiesOrientationNote,
                                    false,
                                    this._sanitizer
                                );
                            });
                            orientationNotes.forEach((item) => {
                                NotesHelper.shapeNote(item, true, this._sanitizer) as NotesAndActivitiesOrientationNote;
                            });
                            const existingAssignmentNote = notesAndActivities.find(
                                (x) =>
                                    (x.noteTypeId === NoteAndActivityTypeEnum.AssignmentNote ||
                                        x.noteTypeId === NoteAndActivityTypeEnum.System) &&
                                    x.noteText?.startsWith('Assigned to')
                            );
                            if (existingAssignmentNote) {
                                return of(
                                    NoteActions.loadNotesAndActivitiesSuccess({
                                        notesAndActivities,
                                        candidateId: action.candidateId,
                                        limitHistory: action.limitHistory,
                                        orientationNotes
                                    })
                                );
                            }
                            return of(
                                NoteActions.loadRecruiterAssignment({
                                    candidateId: action.candidateId,
                                    notesAndActivities,
                                    limitHistory: action.limitHistory,
                                    orientationNotes
                                })
                            );
                        }),
                        catchError((error) =>
                            of(
                                NoteActions.loadNotesAndActivitiesFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
            })
        )
    );

    loadRecruiterAssignmentNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoteActions.loadRecruiterAssignment),
            switchMap((action) =>
                this._candidateService.getRecruiterAssignment(action.candidateId).pipe(
                    map((result) => {
                        let notesAndActivities = action.notesAndActivities;
                        let assignmentNoteText = `Assigned to ${result.recruiterFirstName} ${result.recruiterLastName}`;
                        if (
                            !result.recruiterId &&
                            (result.referralFriendName !== null || result.referralRecruiterName !== null)
                        ) {
                            assignmentNoteText = `Pending Referral- Do Not Contact`;
                        }
                        const assignmentNotes = {
                            userId: UserTypeEnum.User,
                            userName: 'System',
                            noteDateTime: result.updatedDateUtc,
                            note: assignmentNoteText,
                            noteText: assignmentNoteText,
                            noteTypeId: NoteAndActivityTypeEnum.System
                        } as NotesAndActivities;

                        notesAndActivities = notesAndActivities.concat(assignmentNotes);
                        return NoteActions.loadRecruiterAssignmentSuccess({
                            notesAndActivities,
                            candidateId: action.candidateId,
                            limitHistory: action.limitHistory,
                            orientationNotes: action.orientationNotes
                        });
                    }),
                    catchError((error) =>
                        of(
                            NoteActions.loadRecruiterAssignmentFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        )
    );

    addNotesAndActivities$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoteActions.addNotesAndActivities),
            switchMap((action) =>
                this._candidateService.addCandidateNotesAndActivities(action.newNote).pipe(
                    map((result) => NoteActions.addNotesAndActivitiesSuccess({ returnNote: result })),
                    catchError((error) =>
                        of(
                            NoteActions.addNotesAndActivitiesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        )
    );

    addSectionNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoteActions.addSectionNote),
            switchMap((action) =>
                this._candidateService.addCandidateSectionNote(action.newNote).pipe(
                    map((orientationNote) => {
                        const profileNote = { ...orientationNote };
                        NotesHelper.shapeNote(profileNote, false, this._sanitizer) as NotesAndActivitiesOrientationNote;
                        NotesHelper.shapeNote(
                            orientationNote,
                            true,
                            this._sanitizer
                        ) as NotesAndActivitiesOrientationNote;

                        return NoteActions.addSectionNoteSuccess({
                            note: profileNote,
                            orientationNote: orientationNote
                        });
                    }),
                    catchError((error) =>
                        of(
                            NoteActions.addNotesAndActivitiesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        )
    );



    reloadNotes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NoteActions.reloadNotes),
            concatLatestFrom(() => [
                this._store.select(selectNotesAndActivities),
                this._store.select(selectOrientationNotes),
                this._store.select(selectCandidateId),
                this._store.select(selectLimitHistory)
            ]),
            switchMap(([action, latestNotes, latestOrientationNotes, previousCandidateId, limitHistory]) => {
                return this._candidateService
                    .getCandidateNotesAndActivities(action.candidateId, action.limitHistory)
                    .pipe(
                        switchMap((data) => {
                            const notesAndActivities = data.profileNotes;
                            const orientationNotes = data.orientationNotes;
                            notesAndActivities.forEach((item) => {
                                NotesHelper.shapeNote(
                                    item as NotesAndActivitiesOrientationNote,
                                    false,
                                    this._sanitizer
                                );
                            });
                            orientationNotes.forEach((item) => {
                                NotesHelper.shapeNote(item, true, this._sanitizer) as NotesAndActivitiesOrientationNote;
                            });
                            const existingAssignmentNote = notesAndActivities.find(
                                (x) =>
                                    (x.noteTypeId === NoteAndActivityTypeEnum.AssignmentNote ||
                                        x.noteTypeId === NoteAndActivityTypeEnum.System) &&
                                    x.noteText?.startsWith('Assigned to')
                            );
                            if (existingAssignmentNote) {
                                return of(
                                    NoteActions.loadNotesAndActivitiesSuccess({
                                        notesAndActivities,
                                        candidateId: action.candidateId,
                                        limitHistory: action.limitHistory,
                                        orientationNotes
                                    })
                                );
                            }
                            return of(
                                NoteActions.loadRecruiterAssignment({
                                    candidateId: action.candidateId,
                                    notesAndActivities,
                                    limitHistory: action.limitHistory,
                                    orientationNotes
                                })
                            );
                        }),
                        catchError((error) =>
                            of(
                                NoteActions.loadNotesAndActivitiesFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
            })
        )
    );




}
